import React, { useEffect, useState } from 'react';
import '../App.css';
import { fetchDeliveryJson } from '../utils/deliveryApiFetch';
import { proposalAppUrl } from '../ros/sourceLinks';

export function formatMB(bytes) {
  if (bytes > 0 && bytes < 100 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(Math.round(((bytes || 0) / (1024 * 1024)) * 10) / 10).toFixed(1)} MB`;
}

function namesOf(ids, projects) {
  const byId = {};
  (projects || []).forEach((p) => { byId[p.projectId] = p.label; });
  const names = (ids || []).map((id) => byId[id] || `Project #${id}`);
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}


/**
 * A section that opens on request: the title, then one quiet line saying what
 * is inside and what needs attention ("5 locations · 1 missing address
 * detail"). Opening it shows the detail, with its review items first.
 * items: [{ text, href?, linkLabel? }]
 */
export function Collapse({ title, summary, attention, items, open, onToggle, variant, children }) {
  const list = items || [];
  return (
    <div className={`ros-collapse${variant ? ` ros-collapse--${variant}` : ''}${open ? ' ros-collapse--open' : ''}`}>
      <button type="button" className="ros-collapse-head" aria-expanded={!!open} onClick={onToggle}>
        <span className="ros-collapse-text">
          <span className="ros-collapse-title">{title}</span>
          {(summary || attention) && (
            <span className="ros-collapse-summary">
              {summary}
              {summary && attention ? ' · ' : ''}
              {attention && <span className="ros-collapse-attn">{attention}</span>}
            </span>
          )}
        </span>
        <svg className="ros-collapse-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="ros-collapse-body">
          {list.length > 0 && (
            <ul className="ros-collapse-review">
              {list.map((it) => (
                <li key={it.text}>
                  {it.text}
                  {it.href && <> <a className="me-quiet-link" href={it.href} target="_blank" rel="noopener noreferrer">{it.linkLabel || 'Edit in EPS'}</a></>}
                </li>
              ))}
            </ul>
          )}
          {children}
        </div>
      )}
    </div>
  );
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : (many || `${one}s`)}`;

/**
 * What a section's review items amount to, in a few words for its collapsed
 * line: "1 missing address detail", "1 project missing Drive folder".
 * items: [{ code }]
 */
export function attentionText(items) {
  const list = items || [];
  if (!list.length) return '';
  const n = (codes) => list.filter((i) => codes.indexOf(i.code) !== -1).length;
  const parts = [];
  const address = n(['address_missing', 'address_incomplete', 'venue_missing', 'venue_tbd']);
  if (address) parts.push(plural(address, 'missing address detail'));
  const folders = n(['member_folder_missing']);
  if (folders) parts.push(`${plural(folders, 'project')} missing Drive folder`);
  const contacts = n(['contact_missing']);
  if (contacts) parts.push(`${plural(contacts, 'project')} missing a contact`);
  const phones = n(['contact_phone_conflict', 'sales_lead_phone_conflict']);
  if (phones) parts.push(plural(phones, 'phone number conflict'));
  const rest = list.length - address - folders - contacts - phones;
  if (rest) parts.push(`${plural(rest, 'item')} to review`);
  return parts.join(' · ');
}

/** Collapsed-state summaries for Production Information. */
export function productionSummaries(production, locations) {
  const out = { locations: plural((locations || []).length, 'location') };
  if (!production) return { ...out, contacts: 'Loading…', access: 'Loading…', notes: 'Loading…', resources: 'Loading…' };
  const people = new Set((production.eventContacts || []).map((c) => c.name.toLowerCase() || c.line));
  const lead = production.fields.find((f) => f.key === 'salesLead');
  (lead ? lead.groups : []).forEach((g) => people.add(String(g.lines[0] || '').split(' · ')[0].toLowerCase()));
  const notesField = (k) => (production.fields.find((f) => f.key === k) || { groups: [] }).groups.length;
  const accessGroups = (production.accessNotes && production.accessNotes.groups) || [];
  const ownAccess = accessGroups.filter((g) => g.kind !== 'client').length;
  const noteCount = notesField('productionNotes') + notesField('productionUpdates');
  const resourceCount = (production.resources || []).filter((r) => !r.excluded).length;
  return {
    ...out,
    contacts: people.size ? plural(people.size, 'contact') : 'Not set',
    access: ownAccess ? plural(ownAccess, 'note') : accessGroups.length ? 'From the client’s proposal only' : 'Not set',
    notes: noteCount ? plural(noteCount, 'note') : 'Not set',
    resources: resourceCount ? plural(resourceCount, 'resource') : 'No resources added'
  };
}

/**
 * Apply to: this project / selected projects / all projects. The impact line
 * states plainly which projects' Standard packet details change.
 */
/**
 * Which projects an edit applies to:
 *   ○ This project — KLH Group      (where the editor was opened; the default)
 *   ○ Selected projects…            (ticks every packet project right below it)
 *   ○ All packet projects (N)
 * When the editor was opened for several projects at one venue, the first
 * choice names them ("These projects — A and B"). The chosen option is
 * remembered, so the checklist stays open whatever is ticked.
 */
export function ApplyTo({ projects, local, value, onChange, idPrefix, label }) {
  const all = projects.map((p) => p.projectId);
  const here = (local || []).filter((id) => all.indexOf(id) !== -1);
  const same = (a, b) => a.length === b.length && a.every((id) => b.indexOf(id) !== -1);
  const [mode, setMode] = useState(() => {
    if (here.length && same(value, here)) return 'here';
    if (all.length > 1 && value.length === all.length) return 'all';
    return 'some';
  });
  const set = (ids) => onChange(all.filter((id) => ids.indexOf(id) !== -1));
  const choose = (next, ids) => { setMode(next); set(ids); };
  const hereLabel = here.length === 1 ? `This project — ${namesOf(here, projects)}` : `These projects — ${namesOf(here, projects)}`;
  return (
    <div className="me-applyto" role="group" aria-label="Apply to">
      <div className="me-label">{label || 'Apply to'}</div>
      {here.length > 0 && here.length < all.length && (
        <label className="me-applyto-option">
          <input type="radio" name={`${idPrefix}-scope`} checked={mode === 'here'} onChange={() => choose('here', here)} />
          <span>{hereLabel}</span>
        </label>
      )}
      <label className="me-applyto-option">
        <input type="radio" name={`${idPrefix}-scope`} checked={mode === 'some'}
          onChange={() => choose('some', value.length ? value : (here.length ? here : all.slice(0, 1)))} />
        <span>Selected projects…</span>
      </label>
      {mode === 'some' && (
        <div className="me-applyto-list">
          {projects.map((p) => (
            <label key={p.projectId} className="me-applyto-check">
              <input type="checkbox" checked={value.indexOf(p.projectId) !== -1}
                onChange={(e) => set(e.target.checked ? [...value, p.projectId] : value.filter((x) => x !== p.projectId))} />
              <span>{p.label}</span>
            </label>
          ))}
        </div>
      )}
      <label className="me-applyto-option">
        <input type="radio" name={`${idPrefix}-scope`} checked={mode === 'all'} onChange={() => choose('all', all)} />
        <span>All packet projects ({all.length})</span>
      </label>
    </div>
  );
}

// Examples that say what each field is for (Venue Access = how we get in; Site Restrictions = the rules once there).
const PRODUCTION_INFO_PLACEHOLDER = {
  venueAccess: 'e.g. Concord St loading dock · freight elevator B · check in with security',
  siteRestrictions: 'e.g. Floor protection required · no carts through the lobby · out by 11 PM'
};

function FieldEditor({ field, group, projects, local, onCancel, onSave, saving, title }) {
  const start = (group && group.edit) || {};
  const initial = field.key === 'onsiteContact'
    ? { name: start.name || '', phone: start.phone || '', notes: start.notes || '' }
    : { value: start.value || '' };
  const [value, setValue] = useState(initial);
  const [scope, setScope] = useState(group ? group.projectIds : (field.missing || []).slice(0, 1));
  const id = `me-${field.key}`;
  // Venue Access / Site Restrictions: only the field being edited is sent, and only when it changed.
  const payload = field.key === 'productionInfo'
    ? (value.value !== initial.value ? { [field.part]: value.value } : {})
    : value;
  const unchanged = field.key === 'productionInfo' && !Object.keys(payload).length;
  return (
    <div className="me-edit-panel" role="group" aria-label={title || field.label}>
      {/* Production Information fields open under their own block label, so only a contact edit gets a title. */}
      {field.key === 'onsiteContact' && <div className="me-edit-title">{title || field.label}</div>}
      {field.key === 'onsiteContact' ? (
        <>
          {/* Shown from the client's proposal until an onsite contact is set here. */}
          {group && group.current && !start.name && !start.phone && (
            <p className="helper-text me-edit-readonly">Currently from the client’s proposal. Enter a name and phone to set the onsite contact for these projects.</p>
          )}
          <div className="form-row">
            <div className="form-section form-section-half">
              <label className="form-label" htmlFor={`${id}-name`}>Name</label>
              <input id={`${id}-name`} className="input-field" value={value.name} placeholder={(group && group.current && group.current.name) || ''}
                onChange={(e) => setValue({ ...value, name: e.target.value })} />
            </div>
            <div className="form-section form-section-half">
              <label className="form-label" htmlFor={`${id}-phone`}>Phone</label>
              <input id={`${id}-phone`} className="input-field" value={value.phone} placeholder={(group && group.current && group.current.phone) || ''}
                onChange={(e) => setValue({ ...value, phone: e.target.value })} />
            </div>
          </div>
          <div className="form-section">
            <label className="form-label" htmlFor={`${id}-notes`}>Notes</label>
            <textarea id={`${id}-notes`} className="input-field" rows={3} value={value.notes}
              onChange={(e) => setValue({ ...value, notes: e.target.value })} />
          </div>
        </>
      ) : field.key === 'productionInfo' ? (
        <div className="form-section">
          <textarea id={`${id}-${field.part}`} className="input-field" rows={2} aria-label={field.label} value={value.value}
            placeholder={PRODUCTION_INFO_PLACEHOLDER[field.part] || ''}
            onChange={(e) => setValue({ value: e.target.value })} />
        </div>
      ) : (
        <div className="form-section">
          <textarea className="input-field" rows={3} aria-label={field.label} value={value.value}
            onChange={(e) => setValue({ value: e.target.value })} />
        </div>
      )}
      {projects.length > 1 && <ApplyTo projects={projects} local={local} value={scope} onChange={setScope} idPrefix={id} />}
      <p className="me-impact">
        {projects.length === 1
          ? 'Changes made here also appear in Multi-Event packets that include this project.'
          : scope.length
          ? (scope.length === 1
            ? `Changes made here also appear in ${namesOf(scope, projects)}’s Standard packet.`
            : `Changes made here also appear in each chosen project’s Standard packet.`)
          : 'Choose at least one project.'}
      </p>
      <div className="ros-form-actions">
        <button type="button" className="btn-secondary" disabled={!scope.length || saving || unchanged}
          onClick={() => onSave(payload, scope)}>
          {saving ? 'Saving…' : projects.length === 1 ? 'Save' : `Save to ${scope.length} project${scope.length === 1 ? '' : 's'}`}
        </button>
        <button type="button" className="me-action" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}



/**
 * Values still in a project's earlier Parking / Load Dock fields, as the lines
 * they print as inside Venue Access: [{ projectIds, lines: ['Parking: …'] }].
 */
export function earlierAccessLines(loc) {
  return [['parking', 'Parking: '], ['loadDock', 'Load dock: ']].flatMap(([key, prefix]) =>
    ((loc[key] && loc[key].groups) || []).map((g) => ({ key: `${key}:${g.key}`, projectIds: g.projectIds, lines: [prefix + g.lines.join(' ')] })));
}

/**
 * Moving the earlier Parking / Load Dock values into Venue Access, one save
 * per distinct result: each project's Venue Access text followed by its own
 * earlier lines, with Parking and Load Dock cleared. Nothing is reworded.
 * → [{ projectIds, value: { venueAccess, parking: '', loadDock: '' } }]
 */
export function moveIntoVenueAccess(loc) {
  const valueOf = (key, pid) => {
    const g = ((loc[key] && loc[key].groups) || []).find((x) => x.projectIds.indexOf(pid) !== -1);
    return g ? String((g.edit && g.edit.value) || g.lines.join(' ')).trim() : '';
  };
  const byText = {};
  loc.projectIds.forEach((pid) => {
    const parking = valueOf('parking', pid);
    const dock = valueOf('loadDock', pid);
    if (!parking && !dock) return;
    const text = [valueOf('venueAccess', pid), parking && `Parking: ${parking}`, dock && `Load dock: ${dock}`].filter(Boolean).join('\n');
    (byText[text] = byText[text] || []).push(pid);
  });
  return Object.keys(byText).map((text) => ({ projectIds: byText[text], value: { venueAccess: text, parking: '', loadDock: '' } }));
}

// The location fields a saved edit can show right away, by the part the editor sends.
const SAVED_FIELD_KEY = { venueAccess: 'venueAccess', siteRestrictions: 'siteRestrictions', parking: 'parking', loadDock: 'loadDock' };

/**
 * Values just saved, by location field and project: { venueAccess: { '639': 'Use Gate 3' }, … },
 * from an applyRosProjectField save (field, value, the projects it updated).
 */
export function savedValues(prev, fieldKey, value, projectIds) {
  const next = { ...prev };
  const put = (key, text) => {
    next[key] = { ...(next[key] || {}) };
    (projectIds || []).forEach((pid) => { next[key][pid] = String(text || '').trim(); });
  };
  if (fieldKey === 'productionInfo') Object.keys(value || {}).forEach((part) => SAVED_FIELD_KEY[part] && put(SAVED_FIELD_KEY[part], value[part]));
  if (fieldKey === 'productionNotes') put('notes', value && value.value);
  return next;
}

/**
 * A location with just-saved values shown in place, grouped as the server
 * groups them (one value once, with its projects), until the refreshed
 * Production Information arrives and replaces it.
 */
export function withSavedValues(loc, saved) {
  if (!saved || !Object.keys(saved).length) return loc;
  const out = { ...loc };
  Object.keys(saved).forEach((key) => {
    const field = loc[key];
    if (!field) return;
    const changed = Object.keys(saved[key]).filter((pid) => loc.projectIds.indexOf(pid) !== -1);
    if (!changed.length) return;
    const groups = field.groups
      .map((g) => ({ ...g, projectIds: g.projectIds.filter((pid) => changed.indexOf(pid) === -1) }))
      .filter((g) => g.projectIds.length);
    changed.forEach((pid) => {
      const text = saved[key][pid];
      if (!text) return;
      const same = groups.find((g) => g.lines.join(' ') === text);
      if (same) same.projectIds = same.projectIds.concat(pid);
      else groups.push({ key: `saved:${key}:${pid}`, lines: [text], projectIds: [pid], edit: { value: text } });
    });
    out[key] = { groups, missing: loc.projectIds.filter((pid) => !groups.some((g) => g.projectIds.indexOf(pid) !== -1)) };
  });
  return out;
}

/** Applicability inside a location: nothing for a one-project location; "both" / "all N here" when it covers them. */
function localApplies(ids, here, projects) {
  if (here.length <= 1) return '';
  if (ids.length === here.length) return here.length === 2 ? 'Applies to both projects' : `Applies to all ${here.length} projects here`;
  return `Applies to ${namesOf(ids, projects)}`;
}

/** "266 St Margaret St, Charleston, SC 29403" → "266 St Margaret St · Charleston, SC 29403". */
function venueAddressLine(address) {
  const a = String(address || '');
  const i = a.indexOf(', ');
  return i > 0 ? `${a.slice(0, i)} · ${a.slice(i + 2)}` : a;
}

/**
 * A Standard packet's one project: the same block a Multi-Event project opens
 * to, shown directly — no project name to repeat, nothing to open. Review
 * items (an address problem) stay at the top, as inside a Multi-Event group.
 */
function SingleProject({ items, children }) {
  const list = items || [];
  return (
    <div className="me-single">
      {list.length > 0 && (
        <ul className="ros-collapse-review">
          {list.map((it) => (
            <li key={it.text}>
              {it.text}
              {it.href && <> <a className="me-quiet-link" href={it.href} target="_blank" rel="noopener noreferrer">{it.linkLabel || 'Edit in EPS'}</a></>}
            </li>
          ))}
        </ul>
      )}
      {children}
    </div>
  );
}

/**
 * "Edit in Proposal App" for a value the proposal owns: one link for one
 * project; for several, one link per project, named. Nothing for a project
 * without a Project ID.
 */
function ProposalLinks({ projectIds, projects, what }) {
  const ids = (projectIds || []).filter((id) => proposalAppUrl(id));
  if (!ids.length) return null;
  if (ids.length === 1) {
    return <a className="me-icon-action" href={proposalAppUrl(ids[0])} target="_blank" rel="noopener noreferrer"
      aria-label={`Edit ${what || 'this'} in Proposal App`} title="Edit in Proposal App"><Icon kind="external" /></a>;
  }
  return (
    <span className="me-meta me-proposal-links">
      {ids.map((id) => (
        <a key={id} className="me-icon-action me-icon-action--named" href={proposalAppUrl(id)} target="_blank" rel="noopener noreferrer"
          aria-label={`Edit in Proposal App: ${namesOf([id], projects)}`} title="Edit in Proposal App">
          <Icon kind="external" />{namesOf([id], projects)}
        </a>
      ))}
    </span>
  );
}

/** Pencil (edit), plus (add) or arrow-out (opens the owning app). */
function Icon({ kind }) {
  const d = kind === 'add' ? 'M12 5v14M5 12h14'
    : kind === 'external' ? 'M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5'
      : 'M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4';
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
  );
}

/** A quiet icon action: never heavier than the information it changes. `label` is its name and tooltip. */
function Action({ onClick, label, kind }) {
  return (
    <button type="button" className="me-icon-action" onClick={onClick} aria-label={label} title={label}>
      <Icon kind={kind || 'edit'} />
    </button>
  );
}

/** "Name · (555) 555-5555" with the phone kept on one line. */
function PersonLine({ name, phone }) {
  return (
    <span className="me-value">
      {name}{name && phone ? ' · ' : ''}{phone && <span className="me-nowrap">{phone}</span>}
    </span>
  );
}

/**
 * One field's values inside a project group: each value once (with the
 * projects it applies to when that is not all of them here), then what is
 * not set, as a quiet "Not set · Add".
 */
// asTyped: the saved text shows with its own line breaks and blank lines (Production Notes).
function FieldValues({ field, label, here, projects, readOnly, lockedGroup, onEdit, onAdd, editing, renderEditor, asTyped }) {
  const groups = (field && field.groups) || [];
  const missing = (field && field.missing) || [];
  return (
    <div className="me-values">
      {groups.map((g) => (
        <div key={g.key} className="me-value-row">
          <div className={asTyped ? 'me-value-line me-value-line-as-typed' : 'me-value-line'}>
            <span className={asTyped ? 'me-value me-value-as-typed' : 'me-value'}>{g.lines.join(asTyped ? '\n' : ' ')}</span>
            {!readOnly && !(lockedGroup && lockedGroup(g)) && editing !== g.key && <Action onClick={() => onEdit(g)} label={`Edit ${label}`} />}
          </div>
          {localApplies(g.projectIds, here, projects) && <div className="me-meta">{localApplies(g.projectIds, here, projects)}</div>}
          {editing === g.key && renderEditor(g)}
        </div>
      ))}
      {(groups.length === 0 || missing.length > 0) && editing !== '__add' && (
        <div className="me-value-line me-empty-line">
          <span className="me-meta">{groups.length ? `Not set for ${namesOf(missing, projects)}` : (readOnly ? 'None' : 'Not set')}</span>
          {!readOnly && onAdd && <Action kind="add" onClick={onAdd} label={`Add ${label}`} />}
        </div>
      )}
      {editing === '__add' && renderEditor(null)}
    </div>
  );
}

/**
 * Production Information, organized by project: each group is the project
 * (or the projects sharing one venue), then its venue and address, then the
 * operational information — Event Contact, Venue Access (with any value
 * still in the earlier Parking / Load Dock fields and the client's own access
 * details, read-only), Site Restrictions and
 * Production Notes. Identical values show once; differing values show with
 * their projects. Presentation only — each value still lives in its own
 * source field (EPS, Packet Details, the Proposal App, the project's Drive),
 * and only an Edit with "Apply to" writes the projects' Packet Details.
 */
export function SharedProductionSection({ production, loading, error, deliveryApiUrl, identity, userEmail, memberIds, onApplied, epsUrl, open, onToggle, single }) {
  const [editing, setEditing] = useState(null); // { loc, field, group }
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  // Saved values shown at once (the refresh behind them takes several seconds); cleared when it arrives.
  const [saved, setSaved] = useState({});
  useEffect(() => { setSaved({}); }, [production]);
  const isOpen = (k) => !open || open[k];
  const toggle = (k) => onToggle && onToggle(k);
  const projects = production ? production.projects : [];
  const field = (key) => (production && production.fields.find((f) => f.key === key)) || { key, groups: [], missing: [] };

  const apply = async (fieldKey, value, scope, label) => {
    setSaving(true);
    setMessage('');
    try {
      const data = await fetchDeliveryJson(deliveryApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        // A Standard packet saves its one project's field; a Multi-Event packet, the chosen projects'. Same fields either way.
        body: JSON.stringify(single
          ? { action: 'applyProjectField', ...identity, field: fieldKey, value, projectId: scope[0], userEmail }
          : { action: 'applyRosProjectField', ...identity, field: fieldKey, value, projectIds: scope, memberIds, userEmail }),
        mode: 'cors'
      }, { retries: 1 });
      if (data && data.success) {
        setSaved((prev) => savedValues(prev, fieldKey, value, data.updated || scope));
        setEditing(null);
        setMessage(single ? `${label} saved.` : `${label} saved for ${namesOf(data.updated, projects)}.`);
        onApplied();
        return true;
      }
      setMessage((data && data.error) || 'Could not save.');
    } catch (err) {
      setMessage(err.message || 'Network error');
    } finally {
      setSaving(false);
    }
    return false;
  };
  // The coordinator's own click: earlier Parking / Load Dock values move into Venue Access as they are.
  const editingIn = (loc, f) => (editing && editing.loc === loc && editing.field === f ? editing.group : null);
  // An editor launched from a project group: those projects are "This project" in Apply to.
  const editor = (loc, def, group, title) => (
    <FieldEditor field={def} group={group} projects={projects} local={loc.projectIds} saving={saving} title={title}
      onCancel={() => setEditing(null)} onSave={(v, scope) => apply(def.key, v, scope, def.label)} />
  );

  const loadingLine = <div className="helper-text">{error ? '' : (loading || !production ? (single ? 'Reading this project’s production details…' : 'Reading each project’s production details…') : '')}</div>;
  if (!production) {
    return <div className="me-shared">{error && <div className="helper-text pp-error">{error}</div>}{loadingLine}</div>;
  }

  const leads = field('salesLead').groups;
  // Projects whose Production Information is edited in the Proposal App (read-only here).
  const appOwned = new Set((production.projects || []).filter((p) => p.appOwned).map((p) => p.projectId));
  // Production Information: Venue Access (how we get in) and Site Restrictions
  // (the rules once there); Production Notes follows as its own block.
  const INFO = [
    { part: 'venueAccess', label: 'Venue access', editLabel: 'Venue Access' },
    { part: 'siteRestrictions', label: 'Site restrictions', editLabel: 'Site Restrictions' }
  ];

  return (
    <div className="me-shared">
      {message && <div className="helper-text me-message" role="status">{message}</div>}
      {error && <div className="helper-text pp-error">{error}</div>}

      {/* Packet-wide: the Project Sales Lead once, not under every project. */}
      {leads.length > 0 && (
        <div className="me-packetwide">
          <div className="me-label">Project Sales Lead</div>
          {leads.map((g) => {
            const [name, phone] = String(g.lines[0] || '').split(' · ');
            return (
              <div key={g.key} className="me-value-line">
                <PersonLine name={name} phone={phone} />
                {/* The Sales Lead is per project on the EPS, so its scope stays visible. */}
                {!single && <span className="me-meta">{g.projectIds.length === projects.length ? 'All projects' : namesOf(g.projectIds, projects)}</span>}
              </div>
            );
          })}
        </div>
      )}

      {(production.locations || []).map((fromServer) => {
        const loc = withSavedValues(fromServer, saved);
        const here = loc.projectIds;
        const k = `loc:${loc.key}`;
        const items = [];
        if (loc.problem) {
          items.push({
            code: 'address_incomplete',
            text: loc.problem === 'Missing city/state' ? `${loc.name} is missing city/state information.`
              : loc.problem === 'No address' ? `${loc.name} has no address on the EPS.` : `${namesOf(here, projects)} has no venue on the EPS.`,
            // Venue and address belong to the proposal (the EPS mirrors them).
            href: here.length === 1 ? proposalAppUrl(here[0]) : '', linkLabel: 'Edit in Proposal App'
          });
        }
        const title = (
          <span className="me-entity-names">
            {here.map((id) => <span key={id} className="me-entity-name">{namesOf([id], projects)}</span>)}
          </span>
        );
        const summary = [loc.name, loc.cityState].filter(Boolean).join(' · ');
        const notes = { groups: loc.notes.groups.concat(loc.updates.groups),
          missing: loc.notes.missing.filter((id) => !loc.updates.groups.some((g) => g.projectIds.indexOf(id) !== -1)) };
        const inApp = here.length > 0 && here.every((id) => appOwned.has(id));
        const contactEditing = inApp ? null : editingIn(loc.key, 'contact');
        const earlier = earlierAccessLines(loc);
        const Wrap = single ? SingleProject : Collapse;
        return (
          <Wrap key={loc.key} variant="entity" title={title} summary={summary} attention={loc.problem}
            items={items} open={isOpen(k)} onToggle={() => toggle(k)}>
            <div className="me-entity">
              {inApp && (
                <div className="me-meta me-app-owned">
                  Production Information is edited in the Proposal App
                  <ProposalLinks projectIds={here} projects={projects} what="Production Information" />
                </div>
              )}
              <div className="me-ops">
                <section className="me-group-block">
                  <div className="me-label">Venue</div>
                  {/* Name and address are one value; both live on the EPS, so Edit opens it there. */}
                  <div className="me-value-line">
                    <span className="me-value">{loc.name}</span>
                    {/* Name and address come from each project's proposal (the EPS mirrors them). */}
                    <ProposalLinks projectIds={here} projects={projects} what={loc.name} />
                  </div>
                  <div className={loc.address ? 'me-value' : 'me-meta'}>{loc.address ? venueAddressLine(loc.address) : 'No address on the EPS'}</div>
                  {loc.rooms.groups.map((g) => (
                    <div key={g.key} className="me-meta">Room / space: {g.lines.join(' ')}
                      {localApplies(g.projectIds, here, projects) && ` · ${localApplies(g.projectIds, here, projects)}`}</div>
                  ))}
                </section>

                <section className="me-group-block">
                  <div className="me-label me-label-row">{loc.contacts.length > 1 ? 'Day-of contacts' : 'Day-of contact'}
                    {!inApp && contactEditing !== '__add' && <Action kind="add" label="Add day-of contact"
                      onClick={() => setEditing({ loc: loc.key, field: 'contact', group: '__add' })} />}
                  </div>
                  <div className="me-values">
                    {loc.contacts.length === 0 && <div className="me-value-line"><span className="me-meta">Not set</span></div>}
                    {loc.contacts.map((c, i) => {
                      const ck = `person-${i}`;
                      return (
                        <div key={ck} className="me-value-row">
                          <div className="me-value-line">
                            <PersonLine name={c.name} phone={c.phone} />
                            {!inApp && c.onsiteProjectIds.length > 0 && contactEditing !== ck && (
                              <Action onClick={() => setEditing({ loc: loc.key, field: 'contact', group: ck })} label="Edit day-of contact" />
                            )}
                            {/* The client's day-of contact is the proposal's. */}
                            {!c.onsiteProjectIds.length && <ProposalLinks projectIds={c.projectIds} projects={projects} what={c.name} />}
                          </div>
                          {c.notes.map((n) => <div key={n.text} className="me-meta">{n.text}</div>)}
                          {localApplies(c.projectIds, here, projects) && <div className="me-meta">{localApplies(c.projectIds, here, projects)}</div>}
                          {contactEditing === ck && editor(loc, { key: 'onsiteContact', label: 'Day-of contact' },
                            { projectIds: c.onsiteProjectIds, edit: c.edit, current: { name: c.name, phone: c.phone } }, 'Edit day-of contact')}
                        </div>
                      );
                    })}
                    {contactEditing === '__add' && editor(loc, { key: 'onsiteContact', label: 'Day-of contact' }, { projectIds: here, edit: {} }, 'Add day-of contact')}
                  </div>
                </section>

                {INFO.map((a) => {
                  const f = loc[a.part];
                  return (
                    <section key={a.part} className="me-group-block">
                      <div className="me-label">{a.label}</div>
                      <FieldValues field={f} label={a.editLabel} here={here} projects={projects} readOnly={inApp} editing={inApp ? null : editingIn(loc.key, a.part)}
                        onEdit={(g) => setEditing({ loc: loc.key, field: a.part, group: g.key })}
                        onAdd={() => setEditing({ loc: loc.key, field: a.part, group: '__add' })}
                        renderEditor={(g) => editor(loc, { key: 'productionInfo', part: a.part, label: a.editLabel },
                          g ? { projectIds: g.projectIds, edit: g.edit || {} } : { projectIds: f.missing.length ? f.missing : here, edit: {} },
                          `${g ? 'Edit' : 'Add'} ${a.editLabel.toLowerCase()}`)} />
                      {a.part === 'venueAccess' && earlier.length > 0 && (
                        <div className="me-earlier">
                          {earlier.map((g) => (
                            <div key={g.key} className="me-meta">{g.lines[0]}
                              {localApplies(g.projectIds, here, projects) && ` · ${localApplies(g.projectIds, here, projects)}`}</div>
                          ))}
                        </div>
                      )}
                      {a.part === 'venueAccess' && loc.client.groups.map((g) => (
                        <div key={g.key} className="me-meta me-client-line">
                          From the client’s proposal: {g.lines.join(' ')}{localApplies(g.projectIds, here, projects) && ` · ${localApplies(g.projectIds, here, projects)}`}
                        </div>
                      ))}
                    </section>
                  );
                })}

                <section className="me-group-block">
                  <div className="me-label me-label-row">Production notes
                    {/* Drive Production Updates are read-only; a Mayker note can still be added beside them. */}
                    {!inApp && notes.missing.length === 0 && loc.notes.missing.length > 0 && editingIn(loc.key, 'productionNotes') !== '__add' && (
                      <Action kind="add" label="Add production note" onClick={() => setEditing({ loc: loc.key, field: 'productionNotes', group: '__add' })} />
                    )}
                  </div>
                  <FieldValues field={notes} label="Production Notes" asTyped here={here} projects={projects} readOnly={inApp} editing={inApp ? null : editingIn(loc.key, 'productionNotes')}
                    lockedGroup={(g) => loc.updates.groups.indexOf(g) !== -1}
                    onEdit={(g) => setEditing({ loc: loc.key, field: 'productionNotes', group: g.key })}
                    onAdd={() => setEditing({ loc: loc.key, field: 'productionNotes', group: '__add' })}
                    renderEditor={(g) => editor(loc, { key: 'productionNotes', label: 'Production Notes' },
                      g ? { projectIds: g.projectIds, edit: g.edit || {} } : { projectIds: loc.notes.missing.length ? loc.notes.missing : here, edit: {} },
                      g ? 'Edit production notes' : 'Add production notes')} />
                </section>
              </div>
            </div>
          </Wrap>
        );
      })}
      {(production.locations || []).length === 0 && <div className="helper-text me-empty">No event locations yet.</div>}
    </div>
  );
}

const PLACEMENT_TEXT = {
  main: 'Included in packet',
  companion: 'Companion floor-plan PDF',
  link_format: 'Not printable — linked in the packet',
  link_size: 'Too large to print — linked in the packet',
  removed: 'Removed from this packet'
};

function readBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || '').replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error || new Error('Could not read the file'));
    r.readAsDataURL(file);
  });
}

/**
 * Production Resources: a document list. Files stay where they are in Drive;
 * the packet stores references, scope and removals (never copies).
 */
export function ProductionResourcesSection({ production, loading, entries, onEntries, deliveryApiUrl, identity, userEmail, memberIds }) {
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const [picker, setPicker] = useState(null); // Drive files list
  const [scopeFor, setScopeFor] = useState(null);
  const [adding, setAdding] = useState(null); // null | 'drive' | 'upload'
  const [addScope, setAddScope] = useState([]);
  if (!production) return <div className="helper-text">{loading ? 'Looking through each project’s Drive resources…' : ''}</div>;
  const projects = production.projects;
  const plan = production.plan;
  const list = entries || [];

  const setEntry = (fileId, patch) => {
    const i = list.findIndex((e) => e.fileId === fileId);
    const next = list.slice();
    if (i >= 0) next[i] = { ...next[i], ...patch };
    else next.push({ fileId, origin: 'override', addedAt: new Date().toISOString(), ...patch });
    onEntries(next);
  };
  const post = async (action, payload) => fetchDeliveryJson(deliveryApiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify({ action, ...identity, ...payload, members: memberIds.join(','), userEmail }),
    mode: 'cors'
  }, { retries: 1 });

  const remove = (r) => {
    if (r.origin === 'added') onEntries(list.filter((e) => e.fileId !== r.fileId));
    else setEntry(r.fileId, { excluded: true });
  };
  const replace = async (r, file) => {
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) { setMessage('Files over 30 MB: add them in Drive, then use Add from Drive.'); return; }
    setBusy(r.fileId);
    setMessage('');
    try {
      const base64 = await readBase64(file);
      const data = await post('replaceRosResource', { oldFileId: r.fileId, fileName: file.name, mimeType: file.type, base64 });
      if (!data.success) { setMessage(data.error || 'Could not replace the file.'); return; }
      const next = list.filter((e) => e.fileId !== r.fileId);
      next.push({
        fileId: data.file.fileId, origin: 'added', addedAt: new Date().toISOString(), projectIds: r.projectIds,
        kind: r.kind, file: { name: data.file.name, bytes: data.file.bytes, mimeType: data.file.mimeType, mergeable: data.file.mergeable }
      });
      onEntries(next);
      setMessage(`Replaced. "${r.name}" moved to a Superseded folder beside it in Drive.`);
    } catch (err) {
      setMessage(err.message || 'Network error');
    } finally {
      setBusy('');
    }
  };
  // A new file is stored in the first chosen project that has a Drive folder.
  const owners = () => projects.filter((p) => p.hasDriveFolder && addScope.indexOf(p.projectId) !== -1);
  const upload = async (file) => {
    if (!file) return;
    const owner = owners()[0] && owners()[0].projectId;
    if (!owner) { setMessage('None of the chosen projects has a Drive folder listed in EPS to store the file in.'); return; }
    if (file.size > 30 * 1024 * 1024) { setMessage('Files over 30 MB: add them in Drive, then use Add from Drive.'); return; }
    setBusy('upload');
    setMessage('');
    try {
      const base64 = await readBase64(file);
      const data = await post('uploadRosResource', { ownerProjectId: owner, fileName: file.name, mimeType: file.type, base64 });
      if (!data.success) { setMessage(data.error || 'Could not upload the file.'); return; }
      onEntries([...list, {
        fileId: data.file.fileId, origin: 'added', addedAt: new Date().toISOString(), projectIds: addScope,
        file: { name: data.file.name, bytes: data.file.bytes, mimeType: data.file.mimeType, mergeable: data.file.mergeable }
      }]);
      setAdding(null);
      setMessage(`Uploaded to ${namesOf([owner], projects)}’s Production Packet Resources.`);
    } catch (err) {
      setMessage(err.message || 'Network error');
    } finally {
      setBusy('');
    }
  };
  const openPicker = async () => {
    setBusy('picker');
    try {
      const q = new URLSearchParams({ action: 'getRosDriveFiles', ...identity, members: memberIds.join(',') });
      const data = await fetchDeliveryJson(`${deliveryApiUrl}?${q.toString()}`, undefined, { retries: 1 });
      if (data.success) setPicker(data.files || []);
      else setMessage(data.error || 'Could not list Drive files.');
    } catch (err) {
      setMessage(err.message || 'Network error');
    } finally {
      setBusy('');
    }
  };
  const known = {};
  production.resources.forEach((r) => { known[r.fileId] = true; });

  const startAdd = (kind) => {
    setAdding(kind);
    setPicker(null);
    setAddScope(projects.length ? [projects[0].projectId] : []);
    if (kind === 'drive') openPicker();
  };

  return (
    <div className="me-resources">
      {/* File size only matters when it changes what is generated. */}
      {plan.summary.note && (
        <div className="me-size" role="status">
          <div className="me-group-sub">{plan.summary.note}</div>
          {plan.mode === 'companions' && (
            <ul className="me-size-list">
              {plan.companions.map((c) => (
                <li key={c.unit}>{c.label} — Floor Plans{c.parts > 1 ? ` (${c.parts} parts)` : ''}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {loading && <div className="helper-text">Refreshing…</div>}
      {message && <div className="helper-text me-message" role="status">{message}</div>}
      <ul className="me-list">
        {production.resources.length === 0 && <li className="helper-text me-empty">No resources found in the selected projects’ Drive folders.</li>}
        {production.resources.map((r) => (
          <li key={r.fileId} className={`me-row${r.excluded ? ' me-row--off' : ''}`}>
            <div className="me-event">
              <div className="me-event-main">
                <div className="me-event-title">
                  <a className="me-doc-link" href={r.url} target="_blank" rel="noopener noreferrer">{r.label}</a>
                </div>
                <div className="me-meta">Included for {r.appliesTo === 'All projects' ? 'all packet projects' : r.appliesTo}</div>
                {r.placement !== 'main' && <div className="me-event-meta">{PLACEMENT_TEXT[r.placement] || ''}</div>}
                {scopeFor === r.fileId && (
                  <div className="me-edit-panel" role="group" aria-label="Include this resource for">
                    <ApplyTo projects={projects} value={r.projectIds} idPrefix={`res-${r.fileId}`} label="Include this resource for"
                      onChange={(ids) => setEntry(r.fileId, { projectIds: ids })} />
                    <div className="ros-form-actions"><button type="button" className="me-action" onClick={() => setScopeFor(null)}>Done</button></div>
                  </div>
                )}
              </div>
              <div className="me-event-action me-actions">
                {r.excluded ? (
                  <button type="button" className="me-action" onClick={() => setEntry(r.fileId, { excluded: false })}>Include</button>
                ) : (
                  <>
                    <button type="button" className="me-action" aria-expanded={scopeFor === r.fileId} aria-label={`Edit projects for ${r.label}`}
                      onClick={() => setScopeFor(scopeFor === r.fileId ? null : r.fileId)}>Edit</button>
                    <label className="me-action me-file-btn">
                      {busy === r.fileId ? 'Replacing…' : 'Replace'}
                      <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                        onChange={(e) => replace(r, e.target.files && e.target.files[0])} />
                    </label>
                    <button type="button" className="me-action" onClick={() => remove(r)}>Remove</button>
                  </>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
      {!adding && (
        <div className="me-resource-actions">
          <button type="button" className="pp-btn-tertiary" onClick={() => startAdd('drive')}>+ Add from Drive</button>
          <button type="button" className="pp-btn-tertiary" onClick={() => startAdd('upload')}>+ Upload new</button>
        </div>
      )}
      {adding && (
        <div className="me-edit-panel" role="group" aria-label="Add production resource">
          <div className="me-edit-title">Add production resource</div>
          <ApplyTo projects={projects} value={addScope} onChange={setAddScope} idPrefix="res-add" label="Include for" />
          {adding === 'upload' ? (
            <div className="ros-form-actions">
              <label className={`btn-secondary me-file-btn${addScope.length ? '' : ' is-disabled'}`}>
                {busy === 'upload' ? 'Uploading…' : 'Choose file to upload'}
                <input type="file" hidden disabled={!addScope.length} accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                  onChange={(e) => upload(e.target.files && e.target.files[0])} />
              </label>
              <button type="button" className="pp-link-btn" onClick={() => setAdding(null)}>Cancel</button>
            </div>
          ) : (
            <>
              {busy === 'picker' && <div className="helper-text">Opening Drive…</div>}
              {picker && (
                <ul className="me-list me-results">
                  {picker.filter((f) => !known[f.fileId]).length === 0 && <li className="helper-text me-empty">Every file in these projects’ folders is already listed.</li>}
                  {picker.filter((f) => !known[f.fileId]).map((f) => (
                    <li key={f.fileId} className="me-row">
                      <EventFile f={f} projects={projects} disabled={!addScope.length} onAdd={() => {
                        onEntries([...list, {
                          fileId: f.fileId, origin: 'added', addedAt: new Date().toISOString(), projectIds: addScope,
                          file: { name: f.name, bytes: f.bytes, mimeType: f.mimeType, mergeable: f.mergeable }
                        }]);
                        setPicker(null);
                        setAdding(null);
                      }} />
                    </li>
                  ))}
                </ul>
              )}
              <div className="ros-form-actions">
                <button type="button" className="pp-link-btn" onClick={() => { setAdding(null); setPicker(null); }}>Cancel</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function EventFile({ f, projects, onAdd, disabled }) {
  return (
    <div className="me-event">
      <div className="me-event-main">
        <div className="me-event-title">{f.name}</div>
        <div className="me-event-sub">{namesOf([f.ownerProjectId], projects)}</div>
        <div className="me-event-meta">{f.path}{f.mergeable ? '' : ' · not printable'}</div>
      </div>
      <div className="me-event-action"><button type="button" className="pp-link-btn" disabled={disabled} onClick={onAdd}>Add</button></div>
    </div>
  );
}
